// Path: src/components/common/StreamingCodeBlock.tsx
// 流式代码块组件：统一渲染代码块（完整或截断），支持深色/浅色双主题
// 即使多轮接收 HTML 代码也能完整放入一个 <pre><code></code></pre> 中
import React, { useState } from 'react';
import { Copy, ArrowRightFromLine, Check } from 'lucide-react';

interface StreamingCodeBlockProps {
  /** 代码内容 */
  code: string;
  /** 语言 */
  language: string;
  /** 是否截断（流式进行中，代码块未闭合） */
  isTruncated?: boolean;
  /** 主题 */
  theme?: 'dark' | 'light';
  /** HTML 时 Apply 按钮 */
  onApplyCode?: (code: string) => void;
}

export const StreamingCodeBlock: React.FC<StreamingCodeBlockProps> = ({
  code,
  language,
  isTruncated = false,
  theme = 'light',
  onApplyCode,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
      alert('复制失败，请稍后重试');
    }
  };

  const isDark = theme === 'dark';

  return (
    <div
      className={`my-3 rounded-lg overflow-hidden border shadow-sm relative group ${
        isDark
          ? 'bg-black/50 border-white/10'
          : 'bg-gray-900 text-gray-100 border-gray-200'
      }`}
    >
      <div
        className={`flex items-center justify-between px-3 py-1.5 border-b ${
          isDark ? 'bg-white/5 border-white/5' : 'bg-gray-800 border-gray-700'
        }`}
      >
        <span
          className={`text-[10px] font-mono font-bold uppercase ${
            isDark ? 'text-gray-500' : 'text-gray-400'
          }`}
        >
          {language}
        </span>
        {isTruncated && (
          <span className="text-[10px] text-yellow-500/70 animate-pulse">Generating...</span>
        )}
      </div>
      <pre
        className={`p-3 pb-8 overflow-x-auto text-xs font-mono whitespace-pre-wrap ${
          isDark ? 'text-gray-300 custom-scrollbar' : 'text-gray-100'
        }`}
      >
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className={`absolute bottom-2 left-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-all opacity-80 hover:opacity-100 shadow-lg z-10 ${
          isDark
            ? 'bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-600/30 backdrop-blur-sm'
            : 'bg-blue-600 hover:bg-blue-500 text-white'
        }`}
        title="Copy code"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      {language === 'html' && onApplyCode && (
        <button
          onClick={() => onApplyCode(code)}
          className={`absolute bottom-2 right-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-all opacity-80 hover:opacity-100 shadow-lg z-10 ${
            isDark
              ? 'bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white border border-green-600/30 backdrop-blur-sm'
              : 'bg-blue-600 hover:bg-blue-500 text-white'
          }`}
          title="Apply to Preview"
        >
          Apply <ArrowRightFromLine size={14} />
        </button>
      )}
    </div>
  );
};

export default StreamingCodeBlock;
